import { useState, useCallback, useEffect } from "react";
import { Message, Conversation, ChatState } from "@/types/chat";
import { ChatService } from "@/services/chatService";
import {
    validateTextMessage,
    validatePdfFile,
    extractPdfTextFirstPage,
} from "@/utils/chatValidation";
import { validateUserContentAppropriate } from "@/utils/moderation";

const chatService = ChatService.getInstance();
const STORAGE_KEY = "fairlegal_conversations";

const QUOTA_EXCEEDED_MESSAGE =
    "Έχετε φτάσει το όριο των 10 αιτημάτων (μηνύματα ή αρχεία PDF) για τις τελευταίες 24 ώρες.";

const getErrorMessage = (error: unknown): string => {
    if (error instanceof Error && error.message.trim()) {
        return error.message;
    }
    return "Προέκυψε σφάλμα κατά την επικοινωνία με το API.";
};

/** Revive timestamp strings back to Date objects after JSON.parse */
const reviveConversations = (conversations: Conversation[]): Conversation[] =>
    conversations.map((conv) => ({
        ...conv,
        messages: conv.messages.map((msg) => ({
            ...msg,
            timestamp: new Date(msg.timestamp),
        })),
    }));

const loadFromStorage = (): Pick<
    ChatState,
    "conversations" | "selectedConversationId"
> => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) {
            return { conversations: [], selectedConversationId: undefined };
        }

        const parsed = JSON.parse(raw) as {
            conversations: Conversation[];
            selectedConversationId?: string;
        };

        return {
            conversations: reviveConversations(parsed.conversations ?? []),
            selectedConversationId: parsed.selectedConversationId,
        };
    } catch {
        return { conversations: [], selectedConversationId: undefined };
    }
};

export const useChat = () => {
    const [state, setState] = useState<ChatState>(() => ({
        ...loadFromStorage(),
        isLoading: false,
        quota: null,
    }));

    const refreshQuota = useCallback(async () => {
        try {
            const quota = await chatService.getQuota();
            setState((prev) => ({
                ...prev,
                quota,
            }));
        } catch {
            // ignore quota refresh errors
        }
    }, []);

    useEffect(() => {
        void refreshQuota();
    }, [refreshQuota]);

    // Persist conversations + selected ID whenever they change
    useEffect(() => {
        try {
            localStorage.setItem(
                STORAGE_KEY,
                JSON.stringify({
                    conversations: state.conversations,
                    selectedConversationId: state.selectedConversationId,
                }),
            );
        } catch {
            // storage quota exceeded or private-mode restriction — silently ignore
        }
    }, [state.conversations, state.selectedConversationId]);

    const createNewConversation = useCallback(() => {
        const newId = Date.now().toString();
        const newConversation: Conversation = {
            id: newId,
            title: `Νέο Session ${new Date().toLocaleDateString("el-GR")}`,
            date: new Date().toLocaleDateString("el-GR"),
            messages: [],
        };

        setState((prev) => ({
            ...prev,
            conversations: [newConversation, ...prev.conversations],
            selectedConversationId: newId,
        }));
    }, []);

    const selectConversation = useCallback((id: string) => {
        setState((prev) => ({
            ...prev,
            selectedConversationId: id,
        }));
    }, []);

    const sendMessage = useCallback(
        async (message: string, scenario: "A" | "B" = "A") => {
            const validationError = validateTextMessage(message, scenario);
            if (validationError) {
                throw new Error(validationError);
            }

            const moderationError = await validateUserContentAppropriate(message);
            if (moderationError) {
                throw new Error(moderationError);
            }

            const quotaSnapshot = await chatService.getQuota();
            setState((prev) => ({ ...prev, quota: quotaSnapshot }));
            const limit = quotaSnapshot.totalLimit ?? 10;
            if (quotaSnapshot.totalUsed >= limit) {
                throw new Error(QUOTA_EXCEEDED_MESSAGE);
            }

            let conversationId = state.selectedConversationId;

            if (!conversationId) {
                conversationId = Date.now().toString();
                const newConversation: Conversation = {
                    id: conversationId,
                    title: `Νέο Session ${new Date().toLocaleDateString("el-GR")}`,
                    date: new Date().toLocaleDateString("el-GR"),
                    messages: [],
                };

                setState((prev) => ({
                    ...prev,
                    conversations: [newConversation, ...prev.conversations],
                    selectedConversationId: conversationId,
                }));
            }

            const userMessage: Message = {
                id: Date.now().toString(),
                content: message,
                role: "user",
                timestamp: new Date(),
            };

            setState((prev) => ({
                ...prev,
                conversations: prev.conversations.map((conv) =>
                    conv.id === conversationId
                        ? { ...conv, messages: [...conv.messages, userMessage] }
                        : conv,
                ),
                isLoading: true,
            }));

            try {
                const response = await chatService.sendMessage(message, scenario);

                const assistantMessage: Message = {
                    id: (Date.now() + 1).toString(),
                    content: response.debiased,
                    role: "assistant",
                    timestamp: new Date(),
                    debiasSourceText: response.originalForDiff,
                };

                setState((prev) => ({
                    ...prev,
                    conversations: prev.conversations.map((conv) =>
                        conv.id === conversationId
                            ? {
                                  ...conv,
                                  messages: [...conv.messages, assistantMessage],
                              }
                            : conv,
                    ),
                }));
            } catch (error) {
                console.error("Error sending message:", error);

                const messageText = getErrorMessage(error);

                const assistantErrorMessage: Message = {
                    id: (Date.now() + 1).toString(),
                    content: `Σφάλμα: ${messageText}`,
                    role: "assistant",
                    timestamp: new Date(),
                };

                setState((prev) => ({
                    ...prev,
                    conversations: prev.conversations.map((conv) =>
                        conv.id === conversationId
                            ? {
                                  ...conv,
                                  messages: [...conv.messages, assistantErrorMessage],
                              }
                            : conv,
                    ),
                }));

                throw new Error(messageText);
            } finally {
                setState((prev) => ({
                    ...prev,
                    isLoading: false,
                }));
                void refreshQuota();
            }
        },
        [refreshQuota, state.selectedConversationId],
    );

    const uploadPdf = useCallback(
        async (file: File) => {
            const validationError = await validatePdfFile(file);
            if (validationError) {
                throw new Error(validationError);
            }

            const pdfText = await extractPdfTextFirstPage(file);
            const pdfModerationError = await validateUserContentAppropriate(pdfText);
            if (pdfModerationError) {
                throw new Error(pdfModerationError);
            }

            const quotaSnapshot = await chatService.getQuota();
            setState((prev) => ({ ...prev, quota: quotaSnapshot }));
            const limit = quotaSnapshot.totalLimit ?? 10;
            if (quotaSnapshot.totalUsed >= limit) {
                throw new Error(QUOTA_EXCEEDED_MESSAGE);
            }

            let conversationId = state.selectedConversationId;

            if (!conversationId) {
                conversationId = Date.now().toString();
                const newConversation: Conversation = {
                    id: conversationId,
                    title: `Νέο Session ${new Date().toLocaleDateString("el-GR")}`,
                    date: new Date().toLocaleDateString("el-GR"),
                    messages: [],
                };

                setState((prev) => ({
                    ...prev,
                    conversations: [newConversation, ...prev.conversations],
                    selectedConversationId: conversationId,
                }));
            }

            const userMessage: Message = {
                id: Date.now().toString(),
                content: `Ανέβηκε αρχείο PDF: ${file.name}`,
                role: "user",
                timestamp: new Date(),
            };

            setState((prev) => ({
                ...prev,
                conversations: prev.conversations.map((conv) =>
                    conv.id === conversationId
                        ? { ...conv, messages: [...conv.messages, userMessage] }
                        : conv,
                ),
                isLoading: true,
            }));

            try {
                const response = await chatService.sendPdf(file);

                const assistantMessage: Message = {
                    id: (Date.now() + 1).toString(),
                    content: response.debiased,
                    role: "assistant",
                    timestamp: new Date(),
                    debiasSourceText: response.originalForDiff,
                };

                setState((prev) => ({
                    ...prev,
                    conversations: prev.conversations.map((conv) =>
                        conv.id === conversationId
                            ? {
                                  ...conv,
                                  messages: [...conv.messages, assistantMessage],
                              }
                            : conv,
                    ),
                }));
            } catch (error) {
                console.error("Error uploading PDF:", error);

                const messageText = getErrorMessage(error);

                const assistantErrorMessage: Message = {
                    id: (Date.now() + 1).toString(),
                    content: `Σφάλμα: ${messageText}`,
                    role: "assistant",
                    timestamp: new Date(),
                };

                setState((prev) => ({
                    ...prev,
                    conversations: prev.conversations.map((conv) =>
                        conv.id === conversationId
                            ? {
                                  ...conv,
                                  messages: [...conv.messages, assistantErrorMessage],
                              }
                            : conv,
                    ),
                }));

                throw new Error(messageText);
            } finally {
                setState((prev) => ({
                    ...prev,
                    isLoading: false,
                }));
                void refreshQuota();
            }
        },
        [refreshQuota, state.selectedConversationId],
    );

    const deleteConversation = useCallback((id: string) => {
        setState((prev) => {
            const filtered = prev.conversations.filter((c) => c.id !== id);
    
            return {
                ...prev,
                conversations: filtered,
                selectedConversationId:
                    prev.selectedConversationId === id
                        ? filtered[0]?.id
                        : prev.selectedConversationId,
            };
        });
    }, []);

    return {
        ...state,
        createNewConversation,
        selectConversation,
        sendMessage,
        uploadPdf,
        deleteConversation,
    };
};